import Phaser from "phaser";
import { GAME } from "../config";
import { getMpClient, type PlayerInfo } from "../net/MultiplayerClient";
import { addFullscreenButton } from "../ui/Fullscreen";
import { syncDomContainerScale } from "../ui/domScale";

/**
 * Сцена «Турнир» / «Друзья».
 *  - Сверху: мой login (для копирования) + статистика
 *  - Слева: добавить друга по логину
 *  - Справа: список друзей с online-индикатором и кнопкой «В БОЙ»
 *  - Снизу: «🌐 СОЗДАТЬ» / «🔑 ПО КОДУ» — комнаты с незнакомцами по 6-значному коду.
 */
export class FriendsScene extends Phaser.Scene {
  private mp = getMpClient();
  private statusText!: Phaser.GameObjects.Text;
  private myLoginText!: Phaser.GameObjects.Text;
  private listContainer!: Phaser.GameObjects.Container;
  private mpUnsub?: () => void;
  private addInput?: Phaser.GameObjects.DOMElement;

  constructor() { super("Friends"); }

  create() {
    const { WIDTH, HEIGHT } = GAME;
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    this.add.text(WIDTH / 2, 28, "⚔ ТУРНИР", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    const back = this.add.text(60, 28, "← НАЗАД", {
      fontFamily: "Arial Black", fontSize: "14px",
      color: "#ffffff", backgroundColor: "#444444",
      padding: { x: 12, y: 6 } as any,
    } as any).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    back.on("pointerdown", () => this.exit());
    this.input.keyboard!.on("keydown-ESC", () => this.exit());
    addFullscreenButton(this);

    this.statusText = this.add.text(WIDTH / 2, 60, "", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#ffd700",
    }).setOrigin(0.5);

    this.buildLeftPanel();
    this.buildRightPanel();
    this.buildBottomBar();

    this.mpUnsub = this.mp.on(e => this.handleEvent(e));

    if (this.mp.isOpen() && this.mp.isAuthed()) {
      this.refreshIdentity();
      this.mp.fetchFriends();
    } else if (this.mp.isOpen()) {
      // не авторизован — закрываем сцену с подсказкой
      this.scene.start("Settings", { required: true });
    } else {
      this.statusText.setText("Соединение...");
      this.mp.connect()
        .then(() => {
          if (!this.mp.isAuthed()) this.scene.start("Settings", { required: true });
          else { this.refreshIdentity(); this.mp.fetchFriends(); }
        })
        .catch(() => this.statusText.setColor("#ff4444").setText("Сервер недоступен"));
    }
  }

  private buildLeftPanel() {
    const cx = 220;
    this.add.text(cx, 95, "ТВОЙ ЛОГИН", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#aaaaaa",
    }).setOrigin(0.5);
    this.add.rectangle(cx, 130, 360, 56, 0x1a0505).setStrokeStyle(3, 0xffd700);
    this.myLoginText = this.add.text(cx, 130, "—", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    const copyBtn = this.add.rectangle(cx, 175, 200, 30, 0x2a4a6a)
      .setStrokeStyle(2, 0x88ccff)
      .setInteractive({ useHandCursor: true });
    const copyTxt = this.add.text(cx, 175, "📋 СКОПИРОВАТЬ", {
      fontFamily: "Arial Black", fontSize: "12px", color: "#ffffff",
    }).setOrigin(0.5);
    copyBtn.on("pointerdown", () => {
      if (!this.mp.login || !navigator.clipboard) return;
      navigator.clipboard.writeText(this.mp.login).then(() => {
        copyTxt.setText("✓ СКОПИРОВАНО");
        this.time.delayedCall(1500, () => copyTxt.setText("📋 СКОПИРОВАТЬ"));
      }).catch(() => {});
    });

    this.add.text(cx, 220, "ДОБАВИТЬ ДРУГА ПО ЛОГИНУ", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#aaaaaa",
    }).setOrigin(0.5);

    // DOM input для логина друга
    const el = document.createElement("input");
    el.type = "text"; el.placeholder = "ник друга";
    el.maxLength = 16;
    el.style.cssText = "width:240px;height:34px;font-size:16px;padding:6px 10px;font-family:'Arial Black',sans-serif;color:#ffd700;background:#0a0303;border:2px solid #c41e3a;border-radius:4px;outline:none;text-align:center;";
    this.addInput = this.add.dom(cx, 260, el).setOrigin(0.5);
    // форс пересчёт DOM-container scale — иначе input при первом открытии «уедет»
    syncDomContainerScale();
    this.time.delayedCall(50, syncDomContainerScale);

    const addBtn = this.add.rectangle(cx, 305, 200, 38, 0x2a6a2a)
      .setStrokeStyle(2, 0x88ff88)
      .setInteractive({ useHandCursor: true });
    this.add.text(cx, 305, "✓ ДОБАВИТЬ", {
      fontFamily: "Arial Black", fontSize: "16px", color: "#ffffff",
    }).setOrigin(0.5);
    addBtn.on("pointerdown", () => {
      const v = (el as HTMLInputElement).value.trim();
      if (!v) { this.statusText.setColor("#ff8888").setText("Введи ник"); return; }
      this.mp.addFriend(v);
      this.statusText.setColor("#88ccff").setText(`Добавляем ${v}...`);
    });

    // separator
    const sep = this.add.graphics();
    sep.lineStyle(1, 0x3a1010, 0.8);
    sep.lineBetween(440, 90, 440, GAME.HEIGHT - 80);
  }

  private buildRightPanel() {
    this.add.text(700, 95, "ТВОИ ДРУЗЬЯ", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#aaaaaa",
    }).setOrigin(0.5);
    this.listContainer = this.add.container(0, 0);
    this.refreshList();
  }

  private buildBottomBar() {
    const { WIDTH, HEIGHT } = GAME;
    const y = HEIGHT - 50;
    const make = (x: number, label: string, color: number, on: () => void) => {
      const r = this.add.rectangle(x, y, 180, 40, color)
        .setStrokeStyle(2, 0xffd700)
        .setInteractive({ useHandCursor: true });
      this.add.text(x, y, label, {
        fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
        stroke: "#000", strokeThickness: 3,
      }).setOrigin(0.5);
      r.on("pointerdown", on);
    };
    make(WIDTH / 2 - 110, "🌐 СОЗДАТЬ КОМНАТУ", 0x2a4a8a,
         () => this.scene.start("Lobby", { mode: "create" }));
    make(WIDTH / 2 + 110, "🔑 ВОЙТИ ПО КОДУ",   0x6a4a2a,
         () => this.scene.start("Lobby", { mode: "join" }));
  }

  private refreshIdentity() {
    if (this.mp.login) this.myLoginText.setText(this.mp.login);
  }

  private refreshList() {
    this.listContainer.removeAll(true);
    const list: PlayerInfo[] = this.mp.friends.slice().sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1;
      return a.login.localeCompare(b.login);
    });

    if (list.length === 0) {
      const t = this.add.text(700, 230, "Пока нет друзей.\nДобавь по логину слева.", {
        fontFamily: "Arial Black", fontSize: "14px", color: "#666666", align: "center",
      }).setOrigin(0.5);
      this.listContainer.add(t);
      return;
    }

    const x0 = 480, w = 470, h = 50, gap = 8;
    const cy0 = 130;
    list.slice(0, 7).forEach((f, i) => {
      const y = cy0 + i * (h + gap);
      const card = this.add.rectangle(x0 + w / 2, y, w, h, 0x1a0808)
        .setStrokeStyle(2, f.online ? 0x44ff44 : 0x3a3a3a, 0.9);
      const dot = this.add.circle(x0 + 22, y, 7, f.online ? 0x44ff44 : 0x666666, 1)
        .setStrokeStyle(1, 0xffffff, 0.4);

      // мини-плашка цвета банданы
      const bcol = f.bandanaColor ? parseInt(f.bandanaColor.replace("#",""),16) : 0xc41e3a;
      const band = this.add.rectangle(x0 + 50, y, 24, 5, bcol).setStrokeStyle(1, 0x000000, 0.6);

      const name = this.add.text(x0 + 70, y, f.login, {
        fontFamily: "Arial Black", fontSize: "16px",
        color: f.online ? "#ffffff" : "#888888",
      }).setOrigin(0, 0.5);

      this.listContainer.add([card, dot, band, name]);

      if (f.online) {
        const btnW = 130, btnH = 34;
        const btnX = x0 + w - btnW / 2 - 8;
        const btn = this.add.rectangle(btnX, y, btnW, btnH, 0x6a1010)
          .setStrokeStyle(2, 0xc41e3a)
          .setInteractive({ useHandCursor: true });
        const txt = this.add.text(btnX, y, "⚔ В БОЙ", {
          fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
          stroke: "#000", strokeThickness: 2,
        }).setOrigin(0.5);
        btn.on("pointerdown", () => this.invite(f));
        this.listContainer.add([btn, txt]);
      } else {
        const txt = this.add.text(x0 + w - 16, y, "офлайн", {
          fontFamily: "Arial Black", fontSize: "12px", color: "#666666",
        }).setOrigin(1, 0.5);
        this.listContainer.add(txt);
      }
    });
  }

  private invite(f: PlayerInfo) {
    this.statusText.setColor("#ffd700").setText(`Зовём ${f.login} в бой...`);
    this.mp.inviteFriend(f.playerId);
  }

  private handleEvent(e: any) {
    switch (e.type) {
      case "registered":
      case "logged_in":
      case "friends":
      case "login_updated":
        this.refreshIdentity();
        this.refreshList();
        break;
      case "friend_added":
        this.statusText.setColor("#88ff88").setText(`✓ Добавлен: ${e.friend.login}`);
        if (this.addInput) (this.addInput.node as HTMLInputElement).value = "";
        this.refreshList();
        break;
      case "friend_status":
      case "friend_renamed":
        this.refreshList();
        break;
      case "invite_sent":
        this.statusText.setText("Ждём друга в бою...");
        this.scene.start("Lobby", { mode: "create" });
        break;
      case "error":
        this.statusText.setColor("#ff4444").setText(`✗ ${e.msg}`);
        break;
    }
  }

  private exit() {
    this.mpUnsub?.();
    this.scene.start("MainMenu");
  }
}
